import type { ComponentProps } from 'react';
import React, { createContext, useCallback, useMemo, useState } from 'react';

import { Dialog } from '@rtkwlf/fenrir-react';

import { Deferrer } from '@packages/utils/deferrer';

export interface ConfirmationParams {
  title: string;
  description: string;
  cancelText: string;
  confirmText: string;
  appearance?: ComponentProps<typeof Dialog.FooterAction>['appearance'];
}

const RESET_TIMEOUT_MS = 1000;

export interface ConfirmationContextValue {
  confirm: (params?: Partial<ConfirmationParams>) => Promise<boolean>;
  confirmed: boolean | undefined;
}

export const ConfirmationContext =
  createContext<ConfirmationContextValue | null>(null);

export const ConfirmationProvider = ({
  children,
  defaultParams,
}: React.PropsWithChildren<{
  defaultParams: ConfirmationParams;
}>) => {
  const [confirmed, setConfirmed] = useState<boolean>();
  const [deferrer, setDeferrer] = useState<Deferrer<boolean>>();

  const [confirmationParams, setConfirmationParams] =
    useState<ConfirmationParams>(defaultParams);

  const confirm: ConfirmationContextValue['confirm'] = useCallback(
    (params) => {
      const newDeferrer = new Deferrer();
      setConfirmed(undefined);
      setDeferrer(newDeferrer);
      setConfirmationParams({ ...defaultParams, ...params });

      return newDeferrer.promise;
    },
    [defaultParams],
  );

  const value: ConfirmationContextValue = useMemo(
    () => ({
      confirm,
      confirmed,
    }),
    [confirm, confirmed],
  );

  const handleConfirm = () => {
    deferrer?.resolve(true);
    setConfirmed(true);
    setDeferrer(undefined);

    setTimeout(() => setConfirmed(undefined), RESET_TIMEOUT_MS);
  };

  const handleCancel = () => {
    deferrer?.resolve(false);
    setConfirmed(false);
    setDeferrer(undefined);

    setTimeout(() => setConfirmed(undefined), RESET_TIMEOUT_MS);
  };

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      <Dialog.Root isModal isOpen={!!deferrer}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.HeaderTitle>{confirmationParams.title}</Dialog.HeaderTitle>
            <Dialog.HeaderDescription>
              {confirmationParams.description}
            </Dialog.HeaderDescription>
          </Dialog.Header>
          <Dialog.Footer>
            <Dialog.FooterCancel onClick={handleCancel}>
              {confirmationParams.cancelText}
            </Dialog.FooterCancel>
            <Dialog.FooterAction
              appearance={confirmationParams.appearance}
              onClick={handleConfirm}
            >
              {confirmationParams.confirmText}
            </Dialog.FooterAction>
          </Dialog.Footer>
          <Dialog.Close onClick={handleCancel} />
        </Dialog.Content>
      </Dialog.Root>
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const ctx = React.useContext(ConfirmationContext);

  if (ctx === null) {
    throw new Error(
      'ConfirmationContext must be used within a ConfirmationContext.Provider.',
    );
  }

  return ctx;
};
